import { Fragment } from 'react';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { DetailFormSelect, DetailFormValue } from 'components/DetailsView';
import { DetailList, DetailListRemoveButton } from 'components/DetailsView/DetailList';
import { DecimalInput } from 'components/DecimalInput';
import { CopyValue } from 'components/CopyValue';
import { DetailValue } from 'components/DetailsView/DetailValue';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { MealPlanDefinitionState as MealPlanDefinitionStateEnum } from 'data/__generated__';

import { mealPlanHourOptions } from '../constants/mealPlanHours';
import type { MealPlanDefinitionFormData } from '../constants/formData';

const STATE_OPTIONS = [
    { value: MealPlanDefinitionStateEnum.Regular, label: 'Active' },
    { value: MealPlanDefinitionStateEnum.Archived, label: 'Archivée' },
];

export const GeneralInfoSection = ({
    mealPlanDefinition,
}: {
    /** Absent en création : le formulaire est alors vierge et sans métadonnées. */
    mealPlanDefinition?: { _id: string; createdAt: number; updatedAt: number };
}) => {
    const { control, register, watch } = useFormContext<MealPlanDefinitionFormData>();
    const { fields, append, remove } = useFieldArray<MealPlanDefinitionFormData>({ name: 'discountRatios' });

    const numberOfSpots = watch('eligibleCategories')?.length ?? 0;

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    {mealPlanDefinition ? <CopyValue value={mealPlanDefinition._id} /> : null}
                    <DetailFormValue
                        label="Nom affiché aux clients"
                        placeholder="Formule Déjeuner"
                        data-test="meal-plan-definition-name"
                        {...register('name', { required: true })}
                    />
                    {mealPlanDefinition ? (
                        <DetailFormSelect<MealPlanDefinitionStateEnum>
                            label="Statut"
                            name="state"
                            options={STATE_OPTIONS}
                            sublabel="Une formule archivée disparaît de l'application mobile, mais reste lisible dans l'historique des commandes."
                        />
                    ) : null}
                    {mealPlanDefinition ? (
                        <>
                            <DetailValue
                                label="Date de création"
                                value={getExtendedFormattedDatetime(mealPlanDefinition.createdAt)}
                            />
                            <DetailValue
                                label="Dernière modification"
                                value={getExtendedFormattedDatetime(mealPlanDefinition.updatedAt)}
                            />
                        </>
                    ) : null}
                </SectionColumn>
                <SectionColumn>
                    <DetailFormSelect<string>
                        label="Début de l'horaire de validité"
                        name="hoursIntervalAvailable.0"
                        options={mealPlanHourOptions}
                    />
                    <DetailFormSelect<string>
                        label="Fin de l'horaire de validité"
                        name="hoursIntervalAvailable.1"
                        options={mealPlanHourOptions}
                    />
                    <DetailList
                        label="Remises (%)"
                        sublabel={`La 1re ligne s'applique à 2 produits, la 2e à 3, etc. ${numberOfSpots ? `Au plus ${Math.max(numberOfSpots - 1, 0)} ligne(s) pour ${numberOfSpots} emplacements.` : ''}`}
                        onAdd={() => append({ percentage: 10 })}
                        addLabel="Ajouter un palier"
                        items={fields.map((field, index) => (
                            <Fragment key={field.id}>
                                <span className="w-24 text-sm text-muted-foreground">{index + 2} produits</span>
                                {/* `DecimalInput` et pas `type="number"` : une remise peut valoir 12,5 %,
                                    et un champ numérique natif relit « 12,5 » selon la locale du
                                    navigateur — au mieux, `step` l'invalide. */}
                                <Controller
                                    control={control}
                                    name={`discountRatios.${index}.percentage`}
                                    rules={{ required: true }}
                                    render={({ field: { onBlur, onChange, ref, value } }) => (
                                        <DecimalInput
                                            ref={ref}
                                            value={value ?? null}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            data-test={`meal-plan-definition-discount-${index}`}
                                        />
                                    )}
                                />
                                <DetailListRemoveButton onClick={() => remove(index)} />
                            </Fragment>
                        ))}
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};
